import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import {
  SG_GET_DEBRIEF_TOKENS,
  DEBRIEF_TOKEN_PAGE_NUMBER,
  SG_SEND_DEBRIEF_TOKEN_EMAIL,
} from "constants/actions";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import { Button } from "components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "components/ui/pagination";
import { Card, CardContent } from "components/ui/card";
import { Input } from "components/ui/input";
import { Badge } from "components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "components/ui/dialog";
import { Separator } from "components/ui/separator";
import { Check, Copy, EllipsisVertical, Loader2 } from "lucide-react";
import SurveyResponse from "briefResponse/Details";
import SurveyProgress from "briefToken/ProgressJson";
import { toast } from "sonner";
import { cn } from "lib/utils";

const truncateToken = (value) => {
  if (!value) return "";
  const trimmed = value.trim();
  if (trimmed.length <= 16) return trimmed;
  return `${trimmed.slice(0, 8)}…${trimmed.slice(-4)}`;
};

const safeFormatDate = (value) => {
  if (!value) return null;
  try {
    return format(new Date(value), "dd MMM yyyy HH:mm");
  } catch (error) {
    console.error("Invalid date provided", value, error);
    return null;
  }
};

const validateEmail = (value) => {
  if (!value) return false;
  const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.[a-zA-Z]{2,})+$/;
  return emailRegex.test(value.trim());
};

const formatDeliveryStatus = (raw) => {
  if (!raw) {
    return { label: "Not sent", tone: "muted" };
  }

  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch (_error) {
      const clean = raw.trim();
      const normalized = clean.toLowerCase();
      const isSuccess =
        normalized.includes("'errorcode': 0") ||
        normalized.includes('"errorcode": 0') ||
        normalized.includes("'message': 'ok'") ||
        normalized.includes('"message": "ok"');

      if (isSuccess) {
        return {
          label: "Sent",
          tone: "success",
        };
      }

      return {
        label: clean.length > 80 ? `${clean.slice(0, 77)}…` : clean,
        tone: "info",
      };
    }
  }

  if (typeof parsed === "object" && parsed !== null) {
    const message = parsed.Message ?? parsed.message ?? parsed.Status ?? parsed.status;
    const recipient = parsed.To ?? parsed.to ?? parsed.recipient;
    const submittedAt = parsed.SubmittedAt ?? parsed.submitted_at ?? parsed.sent_at;
    const errorCode = parsed.ErrorCode ?? parsed.error_code;

    let label = message || "Sent";
    let tone = "info";

    if (label && label.toLowerCase() === "ok") {
      label = "Sent";
    }

    if (!message && errorCode === 0) {
      label = "Sent";
    }

    if (label.toLowerCase() === "sent") {
      tone = "success";
    } else if (errorCode && Number(errorCode) !== 0) {
      tone = "error";
    }

    return {
      label,
      subtitle: recipient ? `To ${recipient}` : undefined,
      timestamp: submittedAt ? safeFormatDate(submittedAt) : undefined,
      tone,
    };
  }

  return { label: String(raw), tone: "info" };
};

const TokenList = ({
  deBriefId,
  employeeId,
  isDemo,
  DelayedLoad,
  searchTerm,
}) => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [activeToken, setActiveToken] = useState(null);
  const [activeProgressToken, setActiveProgressToken] = useState(null);
  const [activeSendToken, setActiveSendToken] = useState(null);
  const [sendEmail, setSendEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [copiedTokenId, setCopiedTokenId] = useState(null);
  const copyTimeoutRef = useRef(null);

    const { get_debrief_tokens } = useSelector(
        (state) => ({
            get_debrief_tokens: state.debrief_token,
        }),
        shallowEqual
    );

  const tokens = get_debrief_tokens?.debrief_token?.results ?? [];
  const totalPages = useMemo(() => {
    const total = get_debrief_tokens?.debrief_token?.count ?? 0;
    const size = get_debrief_tokens?.debrief_token?.page_size ?? 1;
    return total > 0 ? Math.ceil(total / size) : 1;
  }, [get_debrief_tokens?.debrief_token?.count, get_debrief_tokens?.debrief_token?.page_size]);

  const paginationItems = useMemo(() => {
    const pages = Number(totalPages) || 0;
    const current = Number(page) || 1;
    if (pages <= 1) return [];

    const items = [];
    const addPage = (p) => items.push({ type: "page", page: p, active: p === current });
    const addEllipsis = (key) => items.push({ type: "ellipsis", key });

    addPage(1);
    const start = Math.max(2, current - 1);
    const end = Math.min(pages - 1, current + 1);

    if (start > 2) addEllipsis("start");
    for (let p = start; p <= end; p += 1) addPage(p);
    if (end < pages - 1) addEllipsis("end");
    if (pages > 1) addPage(pages);
    return items;
  }, [page, totalPages]);

    useEffect(() => {
    if (!deBriefId && !employeeId) return;

    setIsLoading(true);
    const params = new URLSearchParams({ page: String(page) });

    if (deBriefId) {
      params.append("debrief_schedule", deBriefId);
      if (searchTerm) params.append("survey_token", searchTerm);
    }

    if (employeeId) params.append("employee", employeeId);

    dispatch({
      type: SG_GET_DEBRIEF_TOKENS,
      payload: params.toString(),
    });
  }, [dispatch, deBriefId, employeeId, page, searchTerm]);

  useEffect(() => {
    if (!isLoading) return;
    const timeout = setTimeout(() => setIsLoading(false), 600);
    return () => clearTimeout(timeout);
  }, [isLoading, tokens]);

  useEffect(() => {
    const nextPage = get_debrief_tokens?.pageNumber;
    if (nextPage) setPage(nextPage);
  }, [get_debrief_tokens?.pageNumber]);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
        dispatch({
            type: DEBRIEF_TOKEN_PAGE_NUMBER,
      payload: nextPage,
    });
  };

  const handleCopy = async ({ token, id }) => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      toast.success("Token copied to clipboard");
      setCopiedTokenId(id);
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = setTimeout(() => setCopiedTokenId(null), 2000);
    } catch (error) {
      toast.error("Unable to copy token");
      console.error("Unable to copy token", error);
    }
  };

  const handleSendToken = async ({ tokenId, email }) => {
    if (!tokenId || !validateEmail(email)) {
      toast.error("Enter a valid email address");
      return;
    }

    setIsSending(true);
    try {
      await dispatch({
        type: SG_SEND_DEBRIEF_TOKEN_EMAIL,
        payload: {
          brief_token_id: tokenId,
          email_id: email,
        },
      });
      toast.success(`Token sent to ${email}`);
      DelayedLoad?.();
      setActiveSendToken(null);
      setSendEmail("");
    } catch (error) {
      toast.error("Unable to send token email");
      console.error("Failed to trigger send token email", error);
    } finally {
      setIsSending(false);
    }
  };

    return (
    <div className="space-y-5 p-6">
      <Card className="border-none bg-muted/20 shadow-none">
        <CardContent className="flex flex-col gap-4 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">Debrief Tokens</p>
            <h2 className="text-2xl font-semibold text-foreground">Manage delivery and responses</h2>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Input
              placeholder="Filter by survey token"
              value={searchTerm ?? ""}
              disabled
              className="h-10 w-56"
            />
          </div>
        </CardContent>
      </Card>

      <div className="overflow-hidden rounded-lg border bg-card">
        <div className="flex items-center justify-between border-b px-6 py-3">
          <p className="text-sm text-muted-foreground">
            {tokens.length ? `${tokens.length} tokens on this page` : "No tokens yet"}
          </p>
          {isLoading && <p className="text-xs text-muted-foreground">Refreshing…</p>}
        </div>

        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40 hover:bg-muted/40">
              <TableHead className="min-w-[220px]">Token</TableHead>
              <TableHead className="min-w-[220px]">Schedule</TableHead>
              <TableHead className="min-w-[160px]">Delivery</TableHead>
              <TableHead className="min-w-[120px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tokens.length === 0 && !isLoading && (
              <TableRow>
                <TableCell colSpan={6} className="h-28 text-center text-muted-foreground">
                  {isDemo
                    ? "Generate a token to get started."
                    : "No tokens generated yet."}
                </TableCell>
              </TableRow>
            )}

            {tokens.map((tokenRow) => {
              const {
                id,
                token,
                delivery_status,
                scheduled_at,
                valid_upto,
                failed,
                deleted_on,
                updated_at,
                stored_response,
                employee,
                to_delete,
              } = tokenRow;

              return (
                <TableRow key={id} className="align-top">
                  <TableCell>
                    <div className="flex flex-col gap-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground" title={token}>{truncateToken(token)}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleCopy({ token, id })}
                        >
                          {copiedTokenId === id ? (
                            <Check className="h-3.5 w-3.5" />
                          ) : (
                            <Copy className="h-3.5 w-3.5" />
                          )}
                          <span className="sr-only">Copy token</span>
                        </Button>
                        {copiedTokenId === id && (
                          <span className="text-xs text-muted-foreground">Copied</span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span>#{id}</span>
                        {employee && <span>{employee}</span>}
                      </div>
                      {isDemo && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-fit px-2 text-xs"
                          onClick={() => {
                            setActiveSendToken(tokenRow);
                            setSendEmail(tokenRow?.email ?? "");
                          }}
                        >
                          Send token
                        </Button>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1 text-sm text-muted-foreground">
                      {scheduled_at && (
                        <span>Scheduled {safeFormatDate(scheduled_at)}</span>
                      )}
                      {valid_upto && (
                        <span>Valid until {safeFormatDate(valid_upto)}</span>
                      )}
                      {deleted_on && (
                        <span>{failed ? "Cancelled" : "Completed"} {safeFormatDate(deleted_on)}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const delivery = formatDeliveryStatus(delivery_status);
                      const badgeClassName = cn(
                        "w-fit text-xs uppercase border-transparent",
                        delivery.tone === "success" && "bg-emerald-500/15 text-emerald-600",
                        delivery.tone === "error" && "bg-red-500/15 text-red-600",
                        delivery.tone === "warning" && "bg-amber-500/20 text-amber-600",
                        (!delivery.tone || delivery.tone === "info") && "bg-muted text-muted-foreground"
                      );

                      return (
                        <div className="flex flex-col gap-2 text-sm">
                           <Badge variant="outline" className={badgeClassName}>
                             {delivery.label}
                           </Badge>
                           {delivery.subtitle && (
                             <span className="text-xs text-muted-foreground">{delivery.subtitle}</span>
                           )}
                           {delivery.timestamp && (
                             <span className="text-xs text-muted-foreground">{delivery.timestamp}</span>
                           )}
                           {updated_at && (
                             <span className="text-xs text-muted-foreground">
                               Updated {safeFormatDate(updated_at)}
                             </span>
                           )}
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setActiveSendToken(tokenRow);
                          setSendEmail(tokenRow?.email ?? "");
                        }}
                      >
                        Send token
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <EllipsisVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48 bg-white">
                          <DropdownMenuItem onClick={() => setActiveProgressToken(id)}>
                            View delivery
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={!to_delete}
                            className={!to_delete ? "cursor-not-allowed opacity-60" : ""}
                            onClick={() => setActiveToken(token)}
                          >
                            View response
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={!stored_response}
                            className={!stored_response ? "cursor-not-allowed opacity-60" : ""}
                            onClick={() => setActiveProgressToken(id)}
                          >
                            Stored response
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Separator className="bg-border" />

        <div className="flex flex-col items-center justify-between gap-4 px-4 py-3 text-sm text-muted-foreground md:flex-row">
          <p>
            Page {page} of {totalPages}
          </p>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    if (page > 1) handlePageChange(page - 1);
                  }}
                  className={page === 1 ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
              {paginationItems.map((item, idx) =>
                item.type === "ellipsis" ? (
                  <PaginationItem key={`ellipsis-${item.key}-${idx}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={`page-${item.page}`}>
                    <PaginationLink
                      href="#"
                      isActive={item.active}
                      onClick={(event) => {
                        event.preventDefault();
                        handlePageChange(item.page);
                      }}
                    >
                      {item.page}
                    </PaginationLink>
                  </PaginationItem>
                )
              )}
              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    if (page < totalPages) handlePageChange(page + 1);
                  }}
                  className={page === totalPages ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      </div>

      <Dialog open={Boolean(activeToken)} onOpenChange={() => setActiveToken(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Stored response</DialogTitle>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto">
            {activeToken && <SurveyResponse briefToken={activeToken} />}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActiveToken(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(activeSendToken)}
        onOpenChange={(open) => {
          if (!open) {
            setActiveSendToken(null);
            setSendEmail("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send token</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              handleSendToken({ tokenId: activeSendToken?.id, email: sendEmail });
            }}
          >
            <Input
              value={sendEmail}
              onChange={(event) => setSendEmail(event.target.value)}
              placeholder="name@company.com"
              autoFocus
              type="email"
            />
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setActiveSendToken(null);
                  setSendEmail("");
                }}
                type="button"
                disabled={isSending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSending}>
                {isSending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {isSending ? "Sending" : "Send"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(activeProgressToken)} onOpenChange={() => setActiveProgressToken(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Token delivery</DialogTitle>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto">
            {activeProgressToken && <SurveyProgress briefToken={activeProgressToken} />}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActiveProgressToken(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    );
};

export default TokenList;